import { useNavigate } from 'react-router-dom';
import {
  Menu,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MenuDivider,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import {
  SignOutRegular,
  ShieldKeyholeRegular,
  PersonRegular,
  DatabaseRegular,
  PeopleSwapRegular,
  DismissRegular,
} from '@fluentui/react-icons';
import { veracityColors } from '../tokens';
import { useAuth } from '../../providers/AuthProvider';
import { useOrgContext } from '../../providers/DataverseOrgProvider';
import { hasGlobalAdmin } from '../../auth/roles';

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0] ?? '').substring(0, 2).toUpperCase();
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

const useStyles = makeStyles({
  trigger: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px 12px 4px 6px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    borderRadius: '20px',
    color: 'inherit',
    ':hover': { backgroundColor: 'rgba(0,0,0,0.06)' },
  },
  triggerOnDark: {
    color: '#FFFFFF',
    ':hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
  },
  avatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: veracityColors.veracityOrange,
    color: '#FFFFFF',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: tokens.fontWeightSemibold,
    flexShrink: 0,
  },
  avatarOnDark: {
    backgroundColor: 'rgba(255,255,255,0.2)',
  },
  name: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    maxWidth: '160px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  identity: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    minWidth: '220px',
  },
  identityName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: veracityColors.midnightCharcoal,
  },
  identityEmail: {
    fontSize: tokens.fontSizeBase200,
    color: veracityColors.duskBlue,
    marginTop: '2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  envFooter: {
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
    fontSize: tokens.fontSizeBase100,
    color: veracityColors.duskBlue,
  },
});

interface UserMenuProps {
  /** Apply the on-dark color treatment (for dark headers like AppShell). */
  onDark?: boolean;
  /** Open the "View as another user" impersonation dialog. Provided by
   *  AppShell (which owns the dialog); omitted in shells without it, so the
   *  menu item simply doesn't appear there. */
  onViewAsUser?: () => void;
}

/**
 * Header user control: avatar + name → dropdown with identity, environment
 * switch, optional Admin/impersonation shortcuts, and sign-out. Used in both
 * ShellLayout and AppShell. Absorbs what used to be the standalone EnvBadge
 * and the header settings gear.
 */
export function UserMenu({ onDark, onViewAsUser }: UserMenuProps) {
  const styles = useStyles();
  const navigate = useNavigate();
  const { user, roles, logout, impersonation, stopImpersonation } = useAuth();
  const { activeOrg, setActiveOrg, hasDevOrg, orgSupported } = useOrgContext();
  const isAdmin = hasGlobalAdmin(roles);

  if (!user) return null;

  return (
    <Menu>
      <MenuTrigger>
        <button
          type="button"
          className={mergeClasses(styles.trigger, onDark && styles.triggerOnDark)}
          aria-label="User menu"
        >
          <span className={mergeClasses(styles.avatar, onDark && styles.avatarOnDark)}>
            {getInitials(user.name)}
          </span>
          <span className={styles.name}>{user.name}</span>
        </button>
      </MenuTrigger>
      <MenuPopover>
        <div className={styles.identity}>
          <div className={styles.identityName}>{user.name}</div>
          <div className={styles.identityEmail}>{user.email}</div>
        </div>
        <MenuDivider />
        <MenuList>
          {isAdmin && (
            <MenuItem
              icon={<ShieldKeyholeRegular />}
              onClick={() => navigate('/admin')}
            >
              Manage Vera access
            </MenuItem>
          )}
          {/* Impersonation — relocated from the header settings gear. "View as"
              needs AppShell's dialog (onViewAsUser); "Stop" works anywhere via
              the global auth context. */}
          {isAdmin && !impersonation && onViewAsUser && (
            <MenuItem icon={<PeopleSwapRegular />} onClick={onViewAsUser}>
              View as another user
            </MenuItem>
          )}
          {impersonation && (
            <MenuItem icon={<DismissRegular />} onClick={() => void stopImpersonation()}>
              Stop viewing as {impersonation.targetName}
            </MenuItem>
          )}
          {/* Environment switch — only when a dev org is configured (never on a
              prod-only build). Tucked in here since it's rarely needed now that
              we're live; was a standalone header badge. Hidden when the BFF
              serves only one org (e.g. the dev App Service), since switching
              there would leave every Dataverse call CSP-blocked. */}
          {hasDevOrg && orgSupported.prod && orgSupported.dev && (
            <Menu checkedValues={{ env: [activeOrg] }}>
              <MenuTrigger>
                <MenuItem icon={<DatabaseRegular />}>
                  Environment: {activeOrg === 'prod' ? 'Production' : 'Development'}
                </MenuItem>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItemRadio name="env" value="prod" onClick={() => setActiveOrg('prod')}>
                    Production
                  </MenuItemRadio>
                  <MenuItemRadio name="env" value="dev" onClick={() => setActiveOrg('dev')}>
                    Development
                  </MenuItemRadio>
                  <MenuDivider />
                  <div className={styles.envFooter}>Switching reloads the page.</div>
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
          <MenuItem icon={<PersonRegular />} disabled>
            Profile (coming soon)
          </MenuItem>
          <MenuDivider />
          <MenuItem icon={<SignOutRegular />} onClick={() => logout()}>
            Sign out
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
